'use client';

import {
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction
} from 'react';

import {
  findBreedByToken,
  findOptionByToken,
  isSelectedValue,
  kindFromInput,
  normalizeOptions
} from '../hooks/useResultsPanel';
import type { CostCalculatorBreed } from '../data/costCalculatorBreeds';
import type {
  CalculatorInput,
  ResultsFilterModel,
  ResultsToggleOption
} from './resultsPanel.types';

export type UseResultsPanelControlsArgs = {
  inputs: Record<string, string | number | boolean | null>;
  setInputs: Dispatch<
    SetStateAction<Record<string, string | number | boolean | null>>
  >;
  breeds: CostCalculatorBreed[];
  resultsInputs: CalculatorInput[];
};

export const useResultsPanelControls = ({
  inputs,
  setInputs,
  breeds,
  resultsInputs
}: UseResultsPanelControlsArgs) => {
  const handleBreedSelect = useCallback(
    (next: string | null | undefined) => {
      const chosen = findBreedByToken(breeds, next);
      setInputs((prev) => ({
        ...prev,
        breed: next ?? null,
        size: chosen?.size ?? null
      }));
    },
    [breeds, setInputs]
  );

  const buildToggleControl = useCallback(
    (
      input: CalculatorInput,
      tokenLabels: Array<{
        token: string;
        label: string;
        icon: ResultsToggleOption['icon'];
      }>,
      fallbackLabel: string
    ): ResultsFilterModel | null => {
      const options = normalizeOptions(input.options);
      const resolvedOptions = tokenLabels
        .map((tokenLabel) => {
          const option = findOptionByToken(options, tokenLabel.token);
          if (!option) return null;

          const isSelected = isSelectedValue(inputs[input.name], option);
          return {
            key: `${input.name}-${tokenLabel.token}`,
            label: tokenLabel.label,
            icon: tokenLabel.icon,
            isSelected,
            onSelect: () =>
              setInputs((prev) => ({
                ...prev,
                [input.name]: option.value
              }))
          } satisfies ResultsToggleOption;
        })
        .filter(Boolean) as ResultsToggleOption[];

      if (resolvedOptions.length !== tokenLabels.length) return null;

      return {
        type: 'toggle',
        key: `results-${input.legacyId}`,
        label: fallbackLabel,
        options: resolvedOptions
      } satisfies ResultsFilterModel;
    },
    [inputs, setInputs]
  );

  const buildSelectControl = useCallback(
    (input: CalculatorInput): ResultsFilterModel => ({
      type: 'select',
      key: `results-${input.legacyId}`,
      label: input.label ?? input.name,
      options: input.options,
      value: inputs[input.name],
      onChange: (next: unknown) =>
        setInputs((prev) => ({
          ...prev,
          [input.name]:
            (next as string | number | boolean | null | undefined) ?? null
        }))
    }),
    [inputs, setInputs]
  );

  const filters = useMemo<ResultsFilterModel[]>(() => {
    return resultsInputs.map((input) => {
      const kind = kindFromInput(input);

      if (kind === 'age') {
        const toggle = buildToggleControl(
          input,
          [
            { token: 'puppy', label: 'Puppy', icon: 'puppy' },
            { token: 'adult', label: 'Adult', icon: 'adult' }
          ],
          'What age is the dog?'
        );
        if (toggle) return toggle;
      }

      if (kind === 'location') {
        const toggle = buildToggleControl(
          input,
          [
            { token: 'urban', label: 'Urban', icon: 'urban' },
            { token: 'rural', label: 'Rural', icon: 'rural' }
          ],
          'Where do you live in Canada?'
        );
        if (toggle) return toggle;
      }

      return buildSelectControl(input);
    });
  }, [buildSelectControl, buildToggleControl, resultsInputs]);

  return { handleBreedSelect, filters };
};
